import {LoadAfterConfirmationEvents} from "../../../page/elements/loadAfterConfirmation";
import {resolve} from "../../../container";
import {elementFrom} from "../../../common/utils/html";
import {YoutubeService} from "./youtube";
import {EopErrorHandler} from "../../../common/errorHandler";
import {customElement} from "lit/decorators.js";
import {UnLitElement} from "../../../common/elements";

export type VideoInformation = {
    duration?: string;
    title?: string;
    description?: string;
    uploadDate?: string;
};

@customElement("eop-social-youtube-seo")
export class EopSocialYoutubeSeo extends UnLitElement {
    private confirmationAbortController: AbortController;

    public constructor(
        private youtubeService: YoutubeService = resolve(YoutubeService),
        private loadAfterConfirmationEvents: LoadAfterConfirmationEvents = resolve(LoadAfterConfirmationEvents),
        private errorHandler: EopErrorHandler = resolve(EopErrorHandler)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();
        const videoId = this.getAttribute("video-id");
        if (!videoId) {
            return;
        }

        this.confirmationAbortController = this.loadAfterConfirmationEvents.onConfirmation("youtube", async () => {
            try {
                const info = await this.youtubeService.getVideoInfo(videoId);
                return this.addVideoMarkup(info);
            } catch (error) {
                return this.errorHandler.report(error);
            }
        });
    }

    public disconnectedCallback(): void {
        if (this.confirmationAbortController) {
            this.confirmationAbortController.abort();
        }
        super.disconnectedCallback();
    }

    private addVideoMarkup(videoInfo: VideoInformation): void {
        if (this.hasAttribute("itemprop")) {
            return;
        }

        this.setAttribute("itemprop", "video");
        this.setAttribute("itemtype", "http://schema.org/VideoObject");
        this.setAttribute("itemscope", "");

        const videoContainer = this.querySelector(".youtube-player");
        if (videoContainer === null) {
            return;
        }

        if (videoInfo.description) {
            videoContainer.prepend(elementFrom(`<span itemprop="description">${videoInfo.description}</span>`));
        }
        if (videoInfo.uploadDate) {
            videoContainer.prepend(this.metaItemProp("uploadDate", videoInfo.uploadDate));
        }
        if (videoInfo.duration) {
            videoContainer.prepend(this.metaItemProp("duration", videoInfo.duration));
        }
        if (videoInfo.title) {
            videoContainer.prepend(this.metaItemProp("name", videoInfo.title));
        }
    }

    private metaItemProp(itemProp: string, value: string): HTMLElement {
        const htmlElement = elementFrom<HTMLElement>(`<meta itemprop="${itemProp}">`);
        htmlElement.setAttribute("content", value);
        return htmlElement;
    }
}